/**
 * JobMockAI — Sidebar Component
 *
 * Features:
 *  - Collapsible: full (260px) ↔ mini/icon-only (64px). State saved in localStorage.
 *  - Per-user section visibility: sections hidden in profile preferences are skipped.
 *  - Mobile: temporary drawer (no mini mode).
 *  - Navigation uses react-router Link — no full-page reload, no scroll-to-top.
 *  - Desktop sidebar is viewport-height with an internal scrollbar for long nav.
 */
import React, { useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Typography,
  Divider,
  Tooltip,
  IconButton,
  Collapse,
  Chip,
  Button,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Psychology,
  ChevronLeft,
  ChevronRight,
  ExpandLess,
  ExpandMore,
  Tune,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { useAuth } from '../../contexts/AuthContext';
import useFeatureFlag from '../../hooks/useFeatureFlag';
import useInterviewerAccess from '../../hooks/useInterviewerAccess';
import navigationConfig, {
  adminNavigationConfig,
  demoStudioNavigationConfig,
  gamesNavigationConfig,
} from '../../config/navigation';
import { canUseDemoStudio } from '../../utils/demoStudioAccess';
import { resolveHiddenNavSections } from '../../utils/navPreferences';

export const SIDEBAR_WIDTH = 260;
export const SIDEBAR_MINI_WIDTH = 64;

const COLLAPSE_KEY = 'mc_sidebar_collapsed';

const Sidebar = ({ open, onClose }) => {
  const location = useLocation();
  const { user, hasSubscription } = useAuth();
  const { canAccessDashboard, showBecomeInterviewer } = useInterviewerAccess();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Collapse state — persisted in localStorage (desktop only)
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(COLLAPSE_KEY) === 'true'; }
    catch { return false; }
  });

  // Per-section open/closed state — sections are open by default
  const [closedSections, setClosedSections] = useState(() => {
    try {
      const stored = localStorage.getItem('mc_closed_sections');
      return stored ? JSON.parse(stored) : [];
    } catch { return []; }
  });

  const toggleSection = (label) => {
    setClosedSections((prev) => {
      const next = prev.includes(label) ? prev.filter((l) => l !== label) : [...prev, label];
      try { localStorage.setItem('mc_closed_sections', JSON.stringify(next)); } catch {}
      return next;
    });
  };

  const toggleCollapse = () => {
    setCollapsed((prev) => {
      const next = !prev;
      try { localStorage.setItem(COLLAPSE_KEY, String(next)); } catch {}
      return next;
    });
  };

  // On mobile the drawer is always fully expanded regardless of desktop collapse state
  const isCollapsed = collapsed && !isMobile;

  // Hidden sections — essential defaults until the user saves NavPreferences
  const hiddenSections = resolveHiddenNavSections(user?.preferences);

  const isAdmin =
    user?.role === 'ADMIN' ||
    user?.role === 'SUPER_ADMIN' ||
    user?.role === 'admin' ||
    user?.role === 'super_admin';

  const isDemoStudio = canUseDemoStudio(user?.role);
  const { enabled: dailyPuzzleEnabled } = useFeatureFlag('daily_puzzle');
  const { enabled: jobHuntAgentEnabled } = useFeatureFlag('job_hunt_agent');
  const { enabled: campusPlacementEnabled } = useFeatureFlag('campus_placement');
  const { enabled: marketplaceEnabled } = useFeatureFlag('interviewer_marketplace');

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  /**
   * Campus + Marketplace are flag-gated and elevated for candidate discovery:
   * hidden when their flag is off; when on, sit after Practice (Campus then Marketplace).
   */
  const orderedNavGroups = (() => {
    const groups = navigationConfig.filter((g) => {
      if (g.elevateOnFlag === 'campus_placement' && !campusPlacementEnabled) return false;
      if (g.elevateOnFlag === 'interviewer_marketplace' && !marketplaceEnabled) return false;
      return true;
    });

    const elevateAfterPractice = (label) => {
      const idx = groups.findIndex((g) => g.label === label);
      if (idx === -1) return;
      const [group] = groups.splice(idx, 1);
      const practiceIdx = groups.findIndex((g) => g.label === 'Practice');
      let insertAt = practiceIdx === -1 ? 1 : practiceIdx + 1;
      if (label === 'Marketplace') {
        const campusIdx = groups.findIndex((g) => g.label === 'Campus' && g.elevated);
        if (campusIdx !== -1) insertAt = campusIdx + 1;
      }
      groups.splice(insertAt, 0, { ...group, elevated: true });
    };

    if (campusPlacementEnabled) elevateAfterPractice('Campus');
    if (marketplaceEnabled) elevateAfterPractice('Marketplace');
    return groups;
  })();

  const renderNavGroup = (group) => {
    // Skip the whole section if user has hidden it
    if (hiddenSections.includes(group.label)) return null;

    const visibleItems = group.items.filter((item) => {
      if (item.adminOnly && !isAdmin) return false;
      if (item.demoStudioOnly && !isDemoStudio) return false;
      if (item.interviewerOnly && !user?.is_interviewer && !canAccessDashboard) return false;
      if (item.becomeInterviewerOnly && !showBecomeInterviewer) return false;
      if (item.featureFlag === 'job_hunt_agent' && (!jobHuntAgentEnabled || !hasSubscription('premium'))) {
        return false;
      }
      if (item.featureFlag === 'campus_placement' && !campusPlacementEnabled) {
        return false;
      }
      return true;
    });
    if (visibleItems.length === 0) return null;

    const isElevatedCampus = Boolean(group.elevated && group.label === 'Campus');
    const isElevatedMarketplace = Boolean(group.elevated && group.label === 'Marketplace');
    const isElevated = isElevatedCampus || isElevatedMarketplace;
    // Keep elevated discovery sections expanded so they are hard to miss.
    const isSectionOpen = isElevated ? true : !closedSections.includes(group.label);
    const hasActiveItem = visibleItems.some((item) => isActive(item.path));

    const isAccountSection = group.label === 'Account';
    const SectionIcon = group.sectionIcon;

    return (
      <Box
        key={group.label}
        data-testid={
          isElevatedCampus
            ? 'nav-section-campus'
            : isElevatedMarketplace
              ? 'nav-section-marketplace'
              : isAccountSection
                ? 'nav-section-account'
                : undefined
        }
      >
        {!isCollapsed && (
          <ListItemButton
            onClick={() => {
              if (isElevated) return;
              toggleSection(group.label);
            }}
            sx={{
              px: 1.5,
              py: 0.55,
              minHeight: 34,
              borderRadius: 1.5,
              mx: 1,
              mt: isAccountSection ? 1.75 : 1.1,
              bgcolor: isElevated ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
              border: isElevated ? '1px solid' : 'none',
              borderColor: isElevated ? alpha(theme.palette.primary.main, 0.4) : 'transparent',
              '&:hover': {
                bgcolor: isElevated
                  ? alpha(theme.palette.primary.main, 0.14)
                  : alpha(theme.palette.text.primary, 0.04),
              },
            }}
          >
            <ListSubheader
              component="span"
              disableSticky
              sx={{
                bgcolor: 'transparent',
                p: 0,
                lineHeight: '22px',
                fontWeight: isElevated ? 800 : 700,
                fontSize: '0.68rem',
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                color: isElevated || hasActiveItem
                  ? 'primary.main'
                  : 'text.secondary',
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
              }}
            >
              {SectionIcon && (
                <SectionIcon
                  sx={{
                    fontSize: 16,
                    color: isElevated || hasActiveItem ? 'primary.main' : 'text.secondary',
                  }}
                />
              )}
              <span>{group.label}</span>
              {isElevatedCampus && (
                <Chip
                  label="New"
                  size="small"
                  color="primary"
                  sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700 }}
                />
              )}
            </ListSubheader>
            {!isElevated && (
              isSectionOpen
                ? <ExpandLess sx={{ fontSize: 18, color: 'text.secondary' }} />
                : <ExpandMore sx={{ fontSize: 18, color: 'text.secondary' }} />
            )}
          </ListItemButton>
        )}
        {isCollapsed && <Box sx={{ mt: 1, mb: 0.5 }} />}
        <Collapse in={isCollapsed || isSectionOpen} timeout="auto" unmountOnExit>
          <List disablePadding>
            {visibleItems.map((item) => {
              const active = isActive(item.path);
              const Icon = item.icon;

              const button = (
                <ListItemButton
                  component={Link}
                  to={item.path}
                  selected={active}
                  onClick={isMobile ? onClose : undefined}
                  sx={{
                    borderRadius: 1.5,
                    mb: 0.25,
                    py: 0.85,
                    px: isCollapsed ? 1 : 1.25,
                    justifyContent: isCollapsed ? 'center' : 'flex-start',
                    minHeight: 42,
                    color: 'text.primary',
                    '& .MuiListItemIcon-root': {
                      color: active ? 'primary.contrastText' : 'text.secondary',
                    },
                    '&:hover': {
                      bgcolor: alpha(theme.palette.text.primary, 0.05),
                      '& .MuiListItemIcon-root': {
                        color: active ? 'primary.contrastText' : 'text.primary',
                      },
                    },
                    '&.Mui-selected': {
                      bgcolor: 'primary.main',
                      color: 'primary.contrastText',
                      boxShadow: `inset 3px 0 0 ${theme.palette.primary.dark}`,
                      '& .MuiListItemIcon-root': { color: 'primary.contrastText' },
                      '&:hover': { bgcolor: 'primary.dark' },
                    },
                    '&:focus-visible': {
                      outline: '2px solid',
                      outlineColor: 'primary.main',
                      outlineOffset: 2,
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: isCollapsed ? 0 : 38,
                      justifyContent: 'center',
                    }}
                  >
                    <Icon sx={{ fontSize: 22 }} />
                  </ListItemIcon>
                  {!isCollapsed && (
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{
                        fontSize: '0.875rem',
                        fontWeight: active ? 600 : 500,
                        letterSpacing: '-0.01em',
                        lineHeight: 1.3,
                      }}
                    />
                  )}
                </ListItemButton>
              );

              return (
                <ListItem key={item.path} disablePadding sx={{ px: 1 }}>
                  {isCollapsed ? (
                    <Tooltip title={item.label} placement="right">
                      <Box sx={{ width: '100%' }}>{button}</Box>
                    </Tooltip>
                  ) : button}
                </ListItem>
              );
            })}
          </List>
        </Collapse>
      </Box>
    );
  };

  const sidebarContent = (
    <Box
      sx={{
        width: isMobile ? SIDEBAR_WIDTH : (isCollapsed ? SIDEBAR_MINI_WIDTH : SIDEBAR_WIDTH),
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.paper',
        transition: 'width 0.2s ease',
        minHeight: 0,
      }}
      role="navigation"
      aria-label="Main navigation"
    >
      {/* Logo / Brand + collapse toggle */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: isCollapsed ? 0 : 2,
          py: 1.5,
          borderBottom: 1,
          borderColor: 'divider',
          justifyContent: isCollapsed ? 'center' : 'space-between',
          minHeight: 56,
          flexShrink: 0,
        }}
      >
        {!isCollapsed && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Psychology sx={{ fontSize: 28, color: 'primary.main', flexShrink: 0 }} />
            <Typography variant="h6" sx={{ fontWeight: 'bold', letterSpacing: '-0.02em', whiteSpace: 'nowrap' }}>
              JobMockAI
            </Typography>
          </Box>
        )}
        {/* Collapse toggle — desktop only */}
        {!isMobile && (
          <Tooltip title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'} placement="right">
            <IconButton
              size="small"
              onClick={toggleCollapse}
              aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              sx={{ flexShrink: 0 }}
            >
              {isCollapsed ? <ChevronRight fontSize="small" /> : <ChevronLeft fontSize="small" />}
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Navigation groups — independent scroll so long pages don't hide nav */}
      <Box
        data-testid="sidebar-nav-scroll"
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          py: 1,
          scrollbarWidth: 'thin',
          '&::-webkit-scrollbar': { width: 6 },
          '&::-webkit-scrollbar-thumb': {
            bgcolor: 'action.disabled',
            borderRadius: 3,
          },
        }}
      >
        {orderedNavGroups.map(renderNavGroup)}

        {dailyPuzzleEnabled && (
          <>
            <Divider sx={{ my: 1 }} />
            {renderNavGroup(gamesNavigationConfig)}
          </>
        )}

        {isDemoStudio && (
          <>
            <Divider sx={{ my: 1 }} />
            {renderNavGroup(demoStudioNavigationConfig)}
          </>
        )}

        {isAdmin && (
          <>
            <Divider sx={{ my: 1 }} />
            {renderNavGroup(adminNavigationConfig)}
          </>
        )}
      </Box>

      {/* Escape hatch: progressive defaults must not bury "show more" in Profile only */}
      <Box
        sx={{
          flexShrink: 0,
          borderTop: 1,
          borderColor: 'divider',
          px: isCollapsed ? 0.5 : 1.5,
          py: 1.25,
          display: 'flex',
          justifyContent: isCollapsed ? 'center' : 'stretch',
        }}
      >
        {isCollapsed ? (
          <Tooltip title="Customize navigation" placement="right">
            <IconButton
              component={Link}
              to="/profile#nav-preferences"
              size="small"
              aria-label="Customize navigation"
              onClick={isMobile ? onClose : undefined}
              data-testid="sidebar-customize-nav"
            >
              <Tune fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : (
          <Button
            component={Link}
            to="/profile#nav-preferences"
            size="small"
            startIcon={<Tune sx={{ fontSize: 16 }} />}
            onClick={isMobile ? onClose : undefined}
            data-testid="sidebar-customize-nav"
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              justifyContent: 'flex-start',
              color: 'text.secondary',
              px: 1,
              '&:hover': { bgcolor: alpha(theme.palette.text.primary, 0.04) },
            }}
          >
            Customize navigation
          </Button>
        )}
      </Box>
    </Box>
  );

  // Mobile: temporary drawer
  if (isMobile) {
    return (
      <Drawer
        anchor="left"
        open={open}
        onClose={onClose}
        ModalProps={{ keepMounted: true }}
        sx={{ '& .MuiDrawer-paper': { width: SIDEBAR_WIDTH } }}
      >
        {sidebarContent}
      </Drawer>
    );
  }

  // Desktop: permanent viewport-height sidebar with internal nav scroll
  return (
    <Box
      component="nav"
      sx={{
        width: isCollapsed ? SIDEBAR_MINI_WIDTH : SIDEBAR_WIDTH,
        flexShrink: 0,
        transition: 'width 0.2s ease',
        borderRight: 1,
        borderColor: 'divider',
        height: '100%',
        alignSelf: 'stretch',
        bgcolor: 'background.paper',
        minHeight: 0,
      }}
    >
      {sidebarContent}
    </Box>
  );
};

export default Sidebar;
